import { findPath, getConnectionTTT } from './locations.js';

export function absoluteMinute(time) {
  return (time.day - 1) * 1440 + time.hour * 60 + (time.minute ?? 0);
}

// Given a path and required arrival time at destination, returns waypoints for each stop
function buildTransitWaypoints(path, locationDefs, arrivalAtDestination) {
  if (path.length <= 1) return [];
  const arrivalTimes = new Array(path.length);
  arrivalTimes[path.length - 1] = arrivalAtDestination;
  for (let i = path.length - 2; i >= 0; i--) {
    const ttt = getConnectionTTT(locationDefs[path[i]], path[i + 1]);
    arrivalTimes[i] = arrivalTimes[i + 1] - ttt;
  }
  // Return waypoints for path[1..end] — path[0] is origin, already in itinerary
  return path.slice(1).map((loc, i) => ({
    location: loc,
    absoluteMinute: arrivalTimes[i + 1],
  }));
}

// Compute full itinerary for a character given their schedule and location defs
// Returns sorted array of { location, absoluteMinute } waypoints
export function computeItinerary(schedule, locationDefs, day) {
  if (!schedule?.length) return [];
  const dayStart = (day - 1) * 1440;
  const waypoints = [];

  for (let i = 0; i < schedule.length; i++) {
    const entry = schedule[i];
    const nextEntry = schedule[(i + 1) % schedule.length];
    const isWrapping = i === schedule.length - 1;

    waypoints.push({
      location: entry.location,
      absoluteMinute: dayStart + entry.from * 60,
    });

    if (nextEntry.location !== entry.location) {
      const nextArrival = isWrapping
        ? dayStart + 1440 + nextEntry.from * 60
        : dayStart + nextEntry.from * 60;

      const path = findPath(locationDefs, entry.location, nextEntry.location);
      if (path?.length > 1) {
        waypoints.push(...buildTransitWaypoints(path, locationDefs, nextArrival));
      }
    }
  }

  return waypoints.sort((a, b) => a.absoluteMinute - b.absoluteMinute);
}

// Normalises an absolute minute to day-1-relative using itinerary's day length (1440)
function toItineraryMinute(absMinute) {
  return absMinute % 1440;
}

// Returns a character's location at a given absolute minute from their itinerary
// Itinerary repeats every 1440 minutes (one day)
export function getLocationAtMinute(itinerary, absMinute) {
  if (!itinerary?.length) return null;
  const dayMinute = toItineraryMinute(absMinute);
  let location = itinerary[0].location;
  for (const wp of itinerary) {
    const wpMinute = toItineraryMinute(wp.absoluteMinute);
    if (wpMinute <= dayMinute) location = wp.location;
    else break;
  }
  return location;
}

// Returns all location transitions { from, to } during (fromMinute, toMinute]
export function getTransitsDuring(itinerary, fromMinute, toMinute) {
  if (!itinerary?.length) return [];
  const transitions = [];
  let prevLoc = getLocationAtMinute(itinerary, fromMinute);
  const fromDay = toItineraryMinute(fromMinute);
  const toDay = toItineraryMinute(toMinute);

  for (const wp of itinerary) {
    const wpMin = toItineraryMinute(wp.absoluteMinute);
    const inRange = fromDay < toDay
      ? wpMin > fromDay && wpMin <= toDay
      : wpMin > fromDay || wpMin <= toDay; // wraps midnight
    if (!inRange) continue;
    if (wp.location !== prevLoc) {
      transitions.push({ from: prevLoc, to: wp.location });
      prevLoc = wp.location;
    }
  }
  return transitions;
}

const TRAIT_LABELS = {
  cat_ears: 'Cat ears',
  cat_tail:  'Cat tail',
  horns:     'Horns',
  wings:     'Wings',
};

function cap(str) {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
}

export function buildAppearanceLines(appearance, schedule, currentHour) {
  if (!appearance) return { lines: [], currentSchedule: null };
  const lines = [];
  if (appearance.hair)     lines.push(`Hair: ${cap(appearance.hair.color)} · ${cap(appearance.hair.style)}`);
  if (appearance.eyes)     lines.push(`Eyes: ${cap(appearance.eyes.shape)} · ${cap(appearance.eyes.color)}`);
  if (appearance.height)   lines.push(`Height: ${appearance.height}`);
  if (appearance.body)     lines.push(`Body: ${cap(appearance.body)}`);
  if (appearance.chest)    lines.push(`Chest: ${appearance.chest.toUpperCase()}`);
  if (appearance.buttocks) lines.push(`Buttocks: ${cap(appearance.buttocks)}`);
  if (appearance.traits?.length) {
    for (const trait of appearance.traits) {
      const label = TRAIT_LABELS[trait.type] ?? cap(trait.type);
      lines.push(`${label}: ${cap(trait.color)}`);
    }
  }
  if (appearance.wearing) lines.push(`Wearing: ${appearance.wearing}`);
  const currentSchedule = schedule?.find(s => currentHour >= s.from && currentHour <= s.to);
  return { lines, currentSchedule };
}

export function resolveScheduleLocations(characterDefs, currentHour) {
  const locations = {};
  for (const [id, def] of Object.entries(characterDefs)) {
    if (!def.schedule) continue;
    const entry = def.schedule.find(s => currentHour >= s.from && currentHour <= s.to);
    if (entry?.location) locations[id] = entry.location;
  }
  return locations;
}
